// src/hooks/useSuppliers.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PurchaseOrderService } from '../admin/pages/purchase-orders/purchaseOrderService';
import { Supplier } from '../admin/pages/purchase-orders/PurchaseOrder';

export const SUPPLIER_KEYS = {
  all: ['suppliers'] as const,
  lists: () => ['suppliers', 'list'] as const,
  paginated: (page: number, search: string, status: string) =>
    ['suppliers', 'list', page, search, status] as const,
  stats: () => ['suppliers', 'stats'] as const,
};

// FIX: Removed the `cursors` field — seek-based pagination only needs `page`.
export interface SupplierPaginationParams {
  page: number;
  search: string;
  status: string;
  pageSize: number;
  // `cursors` intentionally removed — seek-based pagination uses only `page`
}

// FIX: Passes `params.page` directly to getSuppliersPaginated instead of
// cursor objects. Any page can now be fetched in any order.
export const useSuppliersPaginated = (params: SupplierPaginationParams) => {
  return useQuery({
    queryKey: SUPPLIER_KEYS.paginated(params.page, params.search, params.status),
    queryFn: () =>
      PurchaseOrderService.getSuppliersPaginated(
        params.pageSize,
        params.page,
        params.search || undefined,
        params.status !== 'all' ? params.status === 'active' : undefined
      ),
    staleTime: 1000 * 60 * 3,
    placeholderData: (prev: any) => prev,
  });
};

export const useSupplierStats = () => {
  return useQuery({
    queryKey: SUPPLIER_KEYS.stats(),
    queryFn: () => PurchaseOrderService.getSupplierStats(),
    staleTime: 1000 * 60 * 5,
  });
};

// ── Mutation argument types ───────────────────────────────────────────────────

export interface CreateSupplierArgs {
  supplierData: Omit<Supplier, 'id' | 'createdAt' | 'updatedAt'>;
}

export interface UpdateSupplierArgs {
  supplierId: string;
  supplierData: Omit<Supplier, 'id' | 'createdAt' | 'updatedAt'>;
}

// ── Mutation result type (mirrors brand pattern) ──────────────────────────────

export interface SupplierMutationResult {
  success: boolean;
  message?: string;
  id?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// useSupplierMutations
//
// Mirrors the brand caching pattern:
//   • create  → calls PurchaseOrderService.createSupplier, then invalidates ALL
//               supplier queries so the new item appears in every cached page/filter.
//   • update  → calls PurchaseOrderService.updateSupplier, then invalidates ALL
//               supplier queries so the edited item reflects everywhere immediately.
//   • remove  → calls PurchaseOrderService.deleteSupplier, then invalidates ALL
//               supplier queries (existing behaviour, preserved).
//
// All three mutations return a { success, message?, id? } result so the calling
// component (SupplierDrawer / SuppliersManagement) can show toasts and decide
// whether to close the drawer — exactly as BrandManagement does.
// ─────────────────────────────────────────────────────────────────────────────
export const useSupplierMutations = () => {
  const queryClient = useQueryClient();

  // Invalidate every supplier-related query key so lists, stats, and any
  // open paginated page all re-fetch with fresh data.
  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: SUPPLIER_KEYS.all });
  };

  // ── Create ────────────────────────────────────────────────────────────────
  const create = useMutation<SupplierMutationResult, Error, CreateSupplierArgs>({
    mutationFn: async ({ supplierData }) => {
      const id = await PurchaseOrderService.createSupplier(supplierData);
      return { success: true, id };
    },
    onSuccess: invalidateAll,
    onError: (error) => {
      console.error('Error creating supplier:', error);
    },
  });

  // ── Update ────────────────────────────────────────────────────────────────
  const update = useMutation<SupplierMutationResult, Error, UpdateSupplierArgs>({
    mutationFn: async ({ supplierId, supplierData }) => {
      await PurchaseOrderService.updateSupplier(supplierId, supplierData);
      return { success: true, id: supplierId };
    },
    onSuccess: invalidateAll,
    onError: (error) => {
      console.error('Error updating supplier:', error);
    },
  });

  // ── Remove ────────────────────────────────────────────────────────────────
  const remove = useMutation<void, Error, string>({
    mutationFn: (id: string) => PurchaseOrderService.deleteSupplier(id),
    onSuccess: invalidateAll,
    onError: (error) => {
      console.error('Error deleting supplier:', error);
    },
  });

  return { create, update, remove };
};